import { cn } from '@/lib/utils'
import * as TabsPrimitive from '@radix-ui/react-tabs'
import { cva } from 'class-variance-authority'
import React from 'react'

function Tabs({
  className,
  value: _value,
  ...props
}: Omit<React.ComponentProps<typeof TabsPrimitive.Root>, 'value'> &
  Readonly<{ value?: string | null }>) {
  return (
    <TabsPrimitive.Root
      data-slot='tabs'
      className={cn('flex flex-col', className)}
      value={_value ?? undefined}
      {...props}
    />
  )
}

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentProps<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => {
  return (
    <TabsPrimitive.List
      ref={ref}
      data-slot='tabs-list'
      className={cn('inline-flex w-fit items-center justify-start gap-1', className)}
      {...props}
    />
  )
})
TabsList.displayName = TabsPrimitive.TabsList.displayName

const tabsTriggerClassNames = cva(
  'flex w-full items-center justify-between gap-1 whitespace-nowrap rounded-t border-none bg-neutral-75 p-2 text-sm font-medium outline outline-0 -outline-offset-2 outline-mango-300 focus-visible:outline-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0',
)()
type TabsTriggerProps = React.ComponentProps<typeof TabsPrimitive.Trigger> & { unstyled?: boolean }
const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  TabsTriggerProps
>(({ className, unstyled, asChild, ...props }, ref) => {
  return (
    <TabsPrimitive.Trigger
      ref={ref}
      data-slot='tabs-trigger'
      asChild={asChild}
      className={cn(!unstyled && tabsTriggerClassNames, className)}
      {...props}
    />
  )
})
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName

function TabsContent({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.Content>) {
  return (
    <TabsPrimitive.Content
      data-slot='tabs-content'
      className={cn('outline-none', className)}
      {...props}
    />
  )
}

export { Tabs, TabsContent, TabsList, TabsTrigger, tabsTriggerClassNames, type TabsTriggerProps }
